import { ECardinality, ESdChangeReason, ESymbolCategory, IAction, IActionParameter } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

export interface IAddToBagSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  onSaveAction: (action: IAction, changeReason?: ESdChangeReason) => void;
}

export interface IAddToBagSymbolsGroupState {
  bagKeyInput?: string;
}

class AddToBagSymbolsGroup extends PureSdComponent<
  IAddToBagSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IAddToBagSymbolsGroupState
> {
  constructor(props: IAddToBagSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      bagKeyInput: this.getBagKeyValue() ?? ""
    };
  }

  componentDidUpdate(prevProps: IAddToBagSymbolsGroupProps) {
    const prevKey = prevProps.parameters.find((p) => p.name === "key")?.symbol?.fixedValue;
    const currKey = this.getBagKeyValue();
    if (prevKey !== currKey && currKey !== this.state.bagKeyInput) {
      this.setState({ bagKeyInput: currKey ?? "" });
    }
  }

  toKeyFormat = (value: string) => {
    let result = value.replace(/\s+/g, "_");
    result = _.deburr(result);
    result = result.replace(/[^A-Za-z0-9_]/g, "");
    return result.toUpperCase();
  };

  handleBagKeyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value === "") {
      this.handleBagKeyClear();
      return;
    }
    value = this.toKeyFormat(value);
    this.setState({ bagKeyInput: value });
  };

  handleBagKeyBlur = () => {
    const value = this.toKeyFormat(this.state.bagKeyInput ?? "");
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = value;
    keyParam.symbol.category = ESymbolCategory.fixed;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
    this.setState({ bagKeyInput: value });
  };

  handleBagKeyClear = () => {
    this.setState({ bagKeyInput: "" });
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = undefined;
    keyParam.symbol.category = ESymbolCategory.fixed;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
  };

  getBagKeyValue = () => {
    const keyParam = this.props.parameters.find((p) => p.name === "key");
    return keyParam?.symbol?.fixedValue;
  };

  handleBagItemChange = (symbol: any) => {
    const params = _.cloneDeep(this.props.parameters);
    const bagItemParam = params.find((p) => p.name === "bagItem");
    if (!bagItemParam) return;
    bagItemParam.symbol = symbol;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
  };

  getBagItemSymbol = () => {
    const bagItemParam = this.props.parameters.find((p) => p.name === "bagItem");
    return bagItemParam?.symbol;
  };

  renderKey() {
    return (
      <div>
        <div className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: "6px", marginTop: "8px" }}>
          <ActionParameterTitle parameter={{ name: "Bag Key", Typename: "String", cardinality: ECardinality.One }} />
        </div>
        <KuikaInput
          allowClear
          size="sm"
          style={{ width: "100%" }}
          value={this.state.bagKeyInput}
          onChange={this.handleBagKeyInput}
          onBlur={this.handleBagKeyBlur}
          placeholder="Enter Bag Key"
        />
      </div>
    );
  }

  renderBagItem() {
    return (
      <div style={{ marginTop: 12 }}>
        <div className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: "6px" }}>
          Bag Item (Any - Any)
        </div>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={this.getBagItemSymbol()}
          valueType="Any"
          trimValue={true}
          targetCardinality={ECardinality.Any}
          onSelectedSymbol={this.handleBagItemChange}
          bindingParameter={this.props.parameters.find((p) => p.name === "bagItem")}
          bindingLabel="Bag Item"
        />
      </div>
    );
  }

  render() {
    return (
      <div className="kuika_property-grid-column">
        {this.renderKey()}
        {this.renderBagItem()}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    );
  };
}

const addToBagSymbolsGroup = withContext<IAddToBagSymbolsGroupProps>(
  withSD<IAddToBagSymbolsGroupProps>(AddToBagSymbolsGroup)
);
export { addToBagSymbolsGroup as AddToBagSymbolsGroup };
